import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    CircularProgress,
    Backdrop,
    IconButton,
    Alert,
    Chip
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { getFlashBetSports, getFlashBetCategoriesBySport, checkFlashBetMatchExists, createTasks } from '../Api';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';

interface AddTaskModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface BetsApiIdField {
    id: string;
    value: string;
    isValid: boolean;
    validationMessage: string;
    isValidating: boolean;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ 
    open, 
    onClose,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        sportId: 0,
        categoryId: ''
    });
    const [sports, setSports] = useState<FlashBetSportDto[]>([]);
    const [categories, setCategories] = useState<FlashBetCategoryDto[]>([]);
    const [betsApiIds, setBetsApiIds] = useState<BetsApiIdField[]>([
        { id: '1', value: '', isValid: true, validationMessage: '', isValidating: false }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationTimeouts, setValidationTimeouts] = useState<{ [key: string]: NodeJS.Timeout }>({});
    const [pasteModalOpen, setPasteModalOpen] = useState(false);
    const [pasteText, setPasteText] = useState('');

    useEffect(() => {
        if (open) {
            loadSports();
            resetForm();
        }
        return () => {
            // Clean up validation timeouts
            Object.values(validationTimeouts).forEach(timeout => clearTimeout(timeout));
            setValidationTimeouts({});
        };
    }, [open]);

    const resetForm = () => {
        setFormData({ sportId: 0, categoryId: '' });
        setBetsApiIds([{ id: '1', value: '', isValid: true, validationMessage: '', isValidating: false }]);
        setCategories([]);
        setErrorMessage(null);
        setPasteModalOpen(false);
        setPasteText('');
    };

    const loadSports = async () => {
        try {
            const sportsData = await getFlashBetSports();
            setSports(sportsData);
        } catch (error) {
            console.error('Failed to load sports:', error);
            setErrorMessage('Failed to load sports');
        }
    };

    const loadCategories = async (sportId: number) => {
        setIsLoadingCategories(true);
        try {
            const categoriesData = await getFlashBetCategoriesBySport(sportId);
            setCategories(categoriesData);
            setFormData(prev => ({ ...prev, categoryId: '' })); // Reset category selection
        } catch (error) {
            console.error('Failed to load categories:', error);
            setErrorMessage('Failed to load categories');
        } finally {
            setIsLoadingCategories(false);
        }
    };

    const handleSportChange = (sportId: string) => {
        const numericSportId = parseInt(sportId) || 0;
        setFormData(prev => ({ ...prev, sportId: numericSportId }));
        if (numericSportId > 0) {
            loadCategories(numericSportId);
        } else {
            setCategories([]);
            setFormData(prev => ({ ...prev, categoryId: '' }));
        }
    };

    const validateBetsApiId = async (id: string, value: string) => {
        if (!value.trim()) {
            setBetsApiIds(prev => prev.map(item => 
                item.id === id 
                    ? { ...item, isValid: true, validationMessage: '', isValidating: false }
                    : item
            ));
            return;
        }

        // Check for duplicates
        const duplicateCount = betsApiIds.filter(item => item.value.trim() === value.trim() && item.id !== id).length;
        if (duplicateCount > 0) {
            setBetsApiIds(prev => prev.map(item => 
                item.id === id 
                    ? { ...item, isValid: false, validationMessage: 'Duplicate ID', isValidating: false }
                    : item
            ));
            return;
        }

        // Check if match exists
        setBetsApiIds(prev => prev.map(item => 
            item.id === id 
                ? { ...item, isValidating: true }
                : item
        ));

        try {
            const existsResult = await checkFlashBetMatchExists(value.trim());
            const exists = existsResult?.exists || false;
            
            setBetsApiIds(prev => prev.map(item => 
                item.id === id 
                    ? { 
                        ...item, 
                        isValid: !exists, 
                        validationMessage: exists ? 'Match already exists' : '', 
                        isValidating: false 
                    }
                    : item
            ));
        } catch (error) {
            setBetsApiIds(prev => prev.map(item => 
                item.id === id 
                    ? { ...item, isValid: false, validationMessage: 'Validation failed', isValidating: false }
                    : item
            ));
        }
    };

    const handleBetsApiIdChange = (id: string, value: string) => {
        setBetsApiIds(prev => prev.map(item => 
            item.id === id 
                ? { ...item, value, isValidating: false }
                : item
        ));

        // Clear previous timeout for this field
        if (validationTimeouts[id]) {
            clearTimeout(validationTimeouts[id]);
        }

        // Set new timeout for validation
        const timeout = setTimeout(() => {
            validateBetsApiId(id, value);
        }, 500); // Debounce for 500ms

        setValidationTimeouts(prev => ({
            ...prev,
            [id]: timeout
        }));

        // Clear error message when user starts making changes
        if (errorMessage) {
            setErrorMessage(null);
        }
    };

    const addBetsApiIdField = () => {
        const newId = Date.now().toString();
        setBetsApiIds(prev => [
            ...prev,
            { id: newId, value: '', isValid: true, validationMessage: '', isValidating: false }
        ]);
    };

    const extractIntegersFromText = (text: string): string[] => {
        // Use regex to find integer numbers with at least 6 digits
        const integerMatches = text.match(/\d{6,}/g);
        return integerMatches ? Array.from(new Set(integerMatches)) : []; // Remove duplicates
    };

    const handlePasteFromClipboard = () => {
        setPasteModalOpen(true);
        setPasteText('');
    };

    const handlePasteCancel = () => {
        setPasteModalOpen(false);
        setPasteText('');
    };

    const handlePasteAdd = () => {
        const extractedIds = extractIntegersFromText(pasteText);
        
        if (extractedIds.length === 0) {
            setErrorMessage('No valid integers found in the pasted text');
            return;
        }

        // Clear existing empty fields
        const existingNonEmptyIds = betsApiIds.filter(item => item.value.trim() !== '');
        
        // Create new fields for each extracted ID
        const newFields: BetsApiIdField[] = extractedIds.map((id, index) => ({
            id: (Date.now() + index).toString(),
            value: id,
            isValid: true,
            validationMessage: '',
            isValidating: false
        }));

        // If no existing non-empty fields, replace all; otherwise append
        if (existingNonEmptyIds.length === 0) {
            setBetsApiIds(newFields);
        } else {
            setBetsApiIds([...existingNonEmptyIds, ...newFields]);
        }

        // Trigger validation for new fields
        newFields.forEach(field => {
            setTimeout(() => validateBetsApiId(field.id, field.value), 100);
        });

        setPasteModalOpen(false);
        setPasteText('');
        setErrorMessage(null);
    };

    const removeBetsApiIdField = (idToRemove: string) => {
        if (betsApiIds.length <= 1) return; // Keep at least one field
        
        // Clear timeout for removed field
        if (validationTimeouts[idToRemove]) {
            clearTimeout(validationTimeouts[idToRemove]);
            setValidationTimeouts(prev => {
                const newTimeouts = { ...prev };
                delete newTimeouts[idToRemove];
                return newTimeouts;
            });
        }

        setBetsApiIds(prev => prev.filter(item => item.id !== idToRemove));
    };

    const validateForm = (): boolean => {
        if (!formData.sportId || formData.sportId === 0 || !formData.categoryId) {
            setErrorMessage('Please select both sport and category');
            return false;
        }

        const validBetsApiIds = betsApiIds.filter(item => item.value.trim() && item.isValid);
        if (validBetsApiIds.length === 0) {
            setErrorMessage('Please provide at least one valid Bets API ID');
            return false;
        }

        const hasInvalidFields = betsApiIds.some(item => item.value.trim() && (!item.isValid || item.isValidating));
        if (hasInvalidFields) {
            setErrorMessage('Please fix validation errors before submitting');
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const validBetsApiIds = betsApiIds
                .filter(item => item.value.trim() && item.isValid)
                .map(item => item.value.trim());

            const result = await createTasks({
                sportId: formData.sportId,
                categoryId: formData.categoryId,
                betsApiIds: validBetsApiIds
            });

            if (result && result.length > 0) {
                onSuccess();
                handleClose();
            } else {
                setErrorMessage('Failed to create tasks');
            }
        } catch (error) {
            console.error('Failed to create tasks:', error);
            setErrorMessage('Failed to create tasks');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (isLoading) return; // Prevent closing while loading
        
        // Clean up timeouts
        Object.values(validationTimeouts).forEach(timeout => clearTimeout(timeout));
        setValidationTimeouts({});
        
        resetForm();
        onClose();
    };

    const selectedSport = sports.find(sport => sport.id === formData.sportId);
    const selectedCategory = categories.find(category => category.id === formData.categoryId);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Create New Tasks
            </DialogTitle>
            <DialogContent>
                <Box component="form" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select sport and category, then add Bets API IDs to create import tasks.
                    </Typography>
                    
                    {/* Error Message Display */}
                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                    )}
                    
                    {/* Sport Selection */}
                    <TextField
                        fullWidth
                        label="Sport"
                        select
                        value={formData.sportId}
                        onChange={(e) => handleSportChange(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="">
                            <em>Select a sport</em>
                        </MenuItem>
                        {sports.map((sport) => (
                            <MenuItem key={sport.id} value={sport.id}>
                                {sport.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Category Selection */}
                    <TextField
                        fullWidth
                        label="Category"
                        select
                        value={formData.categoryId}
                        onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                        sx={{ mb: 3 }}
                        disabled={!formData.sportId || formData.sportId === 0 || isLoadingCategories}
                    >
                        <MenuItem value="">
                            <em>{isLoadingCategories ? 'Loading categories...' : 'Select a category'}</em>
                        </MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Selected Sport/Category Display */}
                    {selectedSport && selectedCategory && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Creating tasks for:
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                <Chip label={`Sport: ${selectedSport.name}`} size="small" color="primary" />
                                <Chip label={`Category: ${selectedCategory.name}`} size="small" color="secondary" />
                            </Box>
                        </Box>
                    )}

                    {/* Bets API IDs */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Bets API IDs
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handlePasteFromClipboard}
                            disabled={isLoading}
                        >
                            Add multiple
                        </Button>
                    </Box>
                    
                    {betsApiIds.map((field, index) => (
                        <Box key={field.id} sx={{ mb: 2 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                    fullWidth
                                    label={`Bets API ID ${index + 1}`}
                                    value={field.value}
                                    onChange={(e) => handleBetsApiIdChange(field.id, e.target.value)}
                                    error={field.value.trim() !== '' && !field.isValid}
                                    helperText={field.value.trim() !== '' && field.validationMessage}
                                    placeholder="Enter Bets API ID"
                                    disabled={field.isValidating}
                                    InputProps={{
                                        endAdornment: field.isValidating ? (
                                            <CircularProgress size={20} />
                                        ) : null
                                    }}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={addBetsApiIdField}
                                    title="Add another Bets API ID"
                                >
                                    <Add />
                                </IconButton>
                                {betsApiIds.length > 1 && (
                                    <IconButton
                                        color="error"
                                        onClick={() => removeBetsApiIdField(field.id)}
                                        title="Remove this Bets API ID"
                                    >
                                        <Remove />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    ))}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        • Each ID will be validated to ensure it doesn't already exist as a match<br/>
                        • Duplicate IDs in the form are not allowed<br/>
                        • At least one valid ID is required to create tasks
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Close
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={isLoading || betsApiIds.some(field => field.isValidating)}
                >
                    Create Tasks
                </Button>
            </DialogActions>

            {/* Loading Overlay */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={isLoading}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="inherit" />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Creating tasks...
                    </Typography>
                </Box>
            </Backdrop>

            {/* Paste Modal */}
            <Dialog open={pasteModalOpen} onClose={handlePasteCancel} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Paste Bets API IDs
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Paste text containing Bets API IDs. Numbers will be automatically extracted from the text, 
                        regardless of separators (commas, spaces, new lines).
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Paste your text here"
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        placeholder="Example: 123, 456\n789 890\n101112"
                        sx={{ mt: 1 }}
                    />
                    {pasteText && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Preview of extracted IDs:
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {extractIntegersFromText(pasteText).map((id, index) => (
                                    <Chip key={index} label={id} size="small" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasteCancel}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handlePasteAdd} 
                        variant="contained"
                        disabled={!pasteText.trim() || extractIntegersFromText(pasteText).length === 0}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default AddTaskModal;